import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import cloneDeep from 'lodash/cloneDeep';
import chunk from 'lodash/chunk';
import noop from 'lodash/noop';
import themeable from 'zignal-landscape/lib/themeable';

import MfeModal from '../MfeModal/MfeModal';
import Language from '../Language';

const COL = 5;

/**
 * It allows you to select a list of languages from a predefine set of languages.
 *
 * @storybook Components-Languages--LanguageSelector
 */
const LanguageSelector = ({
  styles,
  isOpen = false,
  onClose,
  className = '',
  languages = [],
  onCancelHandler = noop,
  onSubmitHandler = noop
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  /**
   * `languages` is treated as readonly not just because
   * it is a prop but it should not change in case of cancel.
   */
  const [_languages, setLanguages] = useState(languages.map((lang, index) => ({...lang, originalIndex: index})));

  const toggleIsExpanded = () => setIsExpanded(!isExpanded);

  const onCancelBtnHandler = (e) => {
    e.preventDefault();
    onCancelHandler();
  };

  const onDoneBtnHandler = (e) => {
    e.preventDefault();

    const selectedLangIndexes = [];
    _languages.forEach(lang => {
      if (lang.isSelected) {
        selectedLangIndexes.push(lang);
      }
    });
    onSubmitHandler(selectedLangIndexes);
  };

  /* v8 ignore next */
  const languageClickCallback = (index, isSelected) => {
    const newLanguages = cloneDeep(_languages);
    newLanguages[index].isSelected = isSelected;
    setLanguages(newLanguages);
  };

  const languageComponentList = (langList) => langList.map((lang, index) => (
    <Language
      className={styles.LS_BasicLanguage}
      key={lang.name + lang.label}
      name={lang.name}
      label={lang.label}
      isSelected={lang.isSelected}
      onClickCallback={(isSelected) => languageClickCallback(lang.originalIndex, isSelected)}
    />
  ));

  const basicLanguages = () => {
    const basicLanguages = _languages.filter(lang => lang.isBasic);
    return languageComponentList(basicLanguages);
  };

  const expandedLanguages = () => {
    const notBasicLanguages = _languages.filter(lang => !lang.isBasic);
    const elementsPerColumn = Math.ceil(notBasicLanguages.length/COL);
    const columnsData = chunk(notBasicLanguages, elementsPerColumn);
    return columnsData.map((col, index) => (
      <div key={index} className={styles.LS_Column}>
        { languageComponentList(col) }
      </div>
    ));
  };

  return (
    <MfeModal
      isOpen={isOpen}
      size="xl"
      onBackdropClick={onClose}
      hasBackground={false}
    >
      <div className={classNames(isExpanded ? styles.LS_ContainerExpanded : styles.LS_Container, className)}>

        <div className={classNames(isExpanded ? styles.LS_HeaderExpanded : styles.LS_Header)}>
          <div className={styles.LS_Title}>Languages</div>
          <button type="button" className={styles.LS_ExpandBtn} onClick={toggleIsExpanded}>
            {isExpanded ? '<' : '>'}
          </button>
        </div>

        <div className={styles.LS_Body}>
          <div className={styles.LS_BasicLanguagesPane}>
            { basicLanguages() }
          </div>
          <div className={styles.LS_BodySeparator} />
          { isExpanded &&
          <div className={styles.LS_NotBasicLanguagesPane}>
            { expandedLanguages() }
          </div>
          }
        </div>

        <div className={styles.LS_Footer}>
          <button className={styles.LS_CancelBtn} onClick={onCancelBtnHandler}>Cancel</button>
          <button className={styles.LS_DoneBtn} onClick={onDoneBtnHandler}>Done</button>
        </div>

      </div>

    </MfeModal>
  );
};

LanguageSelector.propTypes = {
  styles: PropTypes.object.isRequired,
  className: PropTypes.string,
  languages: PropTypes.array,
  onCancelHandler: PropTypes.func,
  onSubmitHandler: PropTypes.func,
  isOpen: PropTypes.bool,
};

export default themeable(LanguageSelector);
